import React, { useState } from 'react';
import {
  Button,
  Modal,
  ModalProps,
  notification,
  Typography,
} from '@duxton-web/ui';
import { useQueryClient } from '@tanstack/react-query';

import { useVariableContext } from '../../contexts';
import { VariableState } from '@/constants/variable';
import { useDeleteVariable } from '@/hooks/usePlannerQueries/variable';

const DeleteVariableModal = ({ open, onCancel, onOk }: ModalProps) => {
  if (!open) return null;

  const variableContext = useVariableContext();
  const { currentVariable } = variableContext;

  const queryClient = useQueryClient();

  const variableName = currentVariable?.name;

  const { isPending, mutate } = useDeleteVariable({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['planner', 'variables'],
      });

      onOk(null);

      notification.success({
        message: 'Variable deleted',
        description: `Variable ${variableName} was deleted successfully`,
      });
    },
    onError: (_) => {
      notification.error({
        message: 'Error deleting variable',
        description: `Error deleting variable ${variableName}`,
      });
    },
  });

  return (
    <Modal
      open={open}
      maskClosable={false}
      centered
      width={600}
      title={`Delete ${variableName}`}
      cancelButtonProps={{
        size: 'small',
        disabled: isPending,
      }}
      okButtonProps={{
        size: 'small',
        danger: true,
        disabled: isPending,
        loading: isPending,
      }}
      okText="Delete"
      onCancel={onCancel}
      onOk={() => mutate(variableName)}
    >
      <Typography.Text>
        Are you sure you want to proceed with the deletion of
      </Typography.Text>
      <Typography.Text strong>{` ${variableName}`}</Typography.Text>
      <Typography.Text>?</Typography.Text>
    </Modal>
  );
};

const DeleteVariable = ({
  onDeleteClick,
  onDeleteOk,
  onDeleteCancel,
}: {
  onDeleteClick?: () => void;
  onDeleteOk?: () => void;
  onDeleteCancel?: () => void;
}) => {
  const variableContext = useVariableContext();
  const { currentVariable } = variableContext;

  const [open, setOpen] = useState(false);

  return (
    <>
      {currentVariable?.state === VariableState.ARCHIVED && (
        <Button
          danger
          size="small"
          shape="round"
          type="primary"
          onClick={() => {
            setOpen(true);

            onDeleteClick();
          }}
        >
          Delete
        </Button>
      )}
      <DeleteVariableModal
        open={open}
        onOk={() => {
          setOpen(false);

          onDeleteOk();
        }}
        onCancel={() => {
          setOpen(false);

          onDeleteCancel();
        }}
      />
    </>
  );
};

export default DeleteVariable;
